/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { HttpRequestLibrary, readTalerErrorResponse } from "../http-common.js";
import { HttpStatusCode } from "../http-status-codes.js";
import { createPlatformHttpLib } from "../http.js";
import {
  FailCasesByMethod,
  ResultByMethod,
  opKnownHttpFailure,
  opSuccessFromHttp,
  opUnknownHttpFailure,
} from "../operation.js";
import {
  carefullyParseConfig,
  codecForTalerErrorDetail,
  LibtoolVersion,
  opEmptySuccess,
  opKnownTalerFailure,
  TalerErrorCode,
  TalerPreparedTransferApi,
} from "../index.js";
import {
  codecForPreparedTransferConfig,
  codecForRegistrationResponse,
} from "../types-taler-prepared-transfer.js";

export type TalerPreparedTransferResultByMethod<
  prop extends keyof TalerPreparedTransferHttpClient,
> = ResultByMethod<TalerPreparedTransferHttpClient, prop>;
export type TalerPreparedTransferErrorsByMethod<
  prop extends keyof TalerPreparedTransferHttpClient,
> = FailCasesByMethod<TalerPreparedTransferHttpClient, prop>;

/**
 * Allows Taler clients to prepared wire transfers, enabling recurring
 * wire transfers and optimized transfer flow.
 *
 * https://docs.taler.net/core/api-bank-transfer.html
 */
export class TalerPreparedTransferHttpClient {
  httpLib: HttpRequestLibrary;
  public static readonly PROTOCOL_VERSION = "0:0:0";

  constructor(
    readonly baseUrl: string,
    options: {
      httpClient?: HttpRequestLibrary;
    } = {},
  ) {
    this.httpLib = options.httpClient ?? createPlatformHttpLib();
  }

  static isCompatible(version: string): boolean {
    const compare = LibtoolVersion.compare(this.PROTOCOL_VERSION, version);
    return compare?.compatible ?? false;
  }

  /**
   * https://docs.taler.net/core/api-bank-transfer.html#get--config
   *
   */
  async getConfig() {
    const url = new URL(`config`, this.baseUrl);
    const resp = await this.httpLib.fetch(url.href, {
      method: "GET",
    });
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return carefullyParseConfig(
          "taler-prepared-transfer",
          TalerPreparedTransferHttpClient.PROTOCOL_VERSION,
          resp,
          codecForPreparedTransferConfig(),
        );
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-bank-transfer.html#post--registration
   *
   */
  async register(body: TalerPreparedTransferApi.RegistrationRequest) {
    const url = new URL(`registration`, this.baseUrl);
    const resp = await this.httpLib.fetch(url.href, {
      method: "POST",
      body,
    });
    console.log(body);
    switch (resp.status) {
      case HttpStatusCode.Ok:
        return opSuccessFromHttp(resp, codecForRegistrationResponse());
      case HttpStatusCode.BadRequest:
      case HttpStatusCode.Unauthorized:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.Conflict:
        return opKnownHttpFailure(resp.status, resp);
      default:
        return opUnknownHttpFailure(resp);
    }
  }

  /**
   * https://docs.taler.net/core/api-bank-transfer.html#delete--registration
   *
   */
  async unregister(body: TalerPreparedTransferApi.Unregistration) {
    const url = new URL(`unregistration`, this.baseUrl);
    const resp = await this.httpLib.fetch(url.href, {
      method: "POST",
      body,
    });
    switch (resp.status) {
      case HttpStatusCode.NoContent:
        return opEmptySuccess();
      case HttpStatusCode.BadRequest:
      case HttpStatusCode.Unauthorized:
      case HttpStatusCode.NotFound:
        return opKnownHttpFailure(resp.status, resp);
      case HttpStatusCode.Conflict: {
        const body = await readTalerErrorResponse(resp);
        const details = codecForTalerErrorDetail().decode(body);
        switch (details.code) {
          case TalerErrorCode.BANK_OLD_TIMESTAMP:
          case TalerErrorCode.BANK_BAD_SIGNATURE:
            return opKnownTalerFailure(details.code, details);
          default:
            return opKnownHttpFailure(resp.status, resp, details);
        }
      }
      default:
        return opUnknownHttpFailure(resp);
    }
  }
}
